/**
 * UploadCTI — File upload page with drag-and-drop.
 */

import { useState } from 'react';
import { FileUpload } from '../../../components/FileUpload';
import { useAuth } from '../../../components/AuthProvider';
import { useToast } from '../../../components/ToastProvider';
import { uploadDocument } from '../../../api/documents';

export function UploadCTI() {
  const { user } = useAuth();
  const { showToast } = useToast();
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [lastUpload, setLastUpload] = useState<{ document_id: string; message: string } | null>(null);

  const handleFileSelect = (file: File) => {
    setSelectedFile(file);
    setLastUpload(null);
  };

  const handleSubmit = async () => {
    if (!user || !selectedFile) return;
    setUploading(true);
    setLastUpload(null);

    try {
      const response = await uploadDocument(selectedFile, user.tenantId);
      setLastUpload({
        document_id: response.document_id,
        message: response.message,
      });
      showToast(response.message, 'success');
      setSelectedFile(null);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Upload failed. Please try again.';
      showToast(errorMessage, 'error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-heading-lg font-bold text-slate-text">Upload CTI Document</h1>
        <p className="text-body text-slate-muted mt-1">
          Upload threat intelligence files for automated analysis
        </p>
      </div>

      <FileUpload onFileSelect={handleFileSelect} disabled={uploading} />

      {selectedFile && !uploading && (
        <button
          onClick={handleSubmit}
          className="btn-primary w-full sm:w-auto px-8"
        >
          Upload {selectedFile.name}
        </button>
      )}

      {uploading && (
        <div className="glass-card flex items-center gap-4">
          <div className="w-6 h-6 border-3 border-primary-teal/30 border-t-primary-teal rounded-full animate-spin" />
          <p className="text-body font-medium text-slate-text">Uploading file...</p>
        </div>
      )}

      {lastUpload && (
        <div className="glass-card !border-green-400">
          <h3 className="text-section-sm font-bold text-green-800 mb-2">Upload Successful</h3>
          <p className="text-body text-slate-text">{lastUpload.message}</p>
          <p className="text-[16px] text-slate-muted mt-2">
            Document ID: <code className="font-mono bg-gray-100 px-2 py-1 rounded">{lastUpload.document_id}</code>
          </p>
        </div>
      )}

      {/* Supported formats info */}
      <div className="glass-card">
        <h3 className="text-section-sm font-bold text-slate-text mb-3">Supported Formats</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {['PDF', 'TXT', 'MD', 'CSV', 'JSON', 'STIX', 'IOC', 'YARA', 'YML', 'LOG', 'SYSLOG', 'EVTX'].map(
            (format) => (
              <span
                key={format}
                className="text-[16px] font-medium text-primary-teal bg-primary-teal/10 px-3 py-2 rounded-btn text-center"
              >
                .{format.toLowerCase()}
              </span>
            )
          )}
        </div>
      </div>
    </div>
  );
}
